import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { SearchX } from "lucide-react";

import { visitsApi } from "@/api/scheduling";
import { Alert } from "@/components/Alert";
import { EmptyState } from "@/components/EmptyState";
import { Field } from "@/components/Field";
import { PageBody, PageHeader, PageShell } from "@/components/PageShell";
import { PageSkeleton } from "@/components/PageSkeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

function visitStatusVariant(status) {
  if (status === "completed") return "default";
  if (status === "cancelled") return "secondary";
  return "outline";
}

export function VisitWorkspacePage() {
  const { id } = useParams();
  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);

  const [notes, setNotes] = useState("");
  const [saveStatus, setSaveStatus] = useState("");
  const notesTimeoutRef = useRef(null);

  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [addingTask, setAddingTask] = useState(false);

  const [vitals, setVitals] = useState({
    temperature: "",
    blood_pressure: "",
    pulse: "",
    oxygen_saturation: "",
    weight: "",
  });
  const [savingVitals, setSavingVitals] = useState(false);
  const [vitalsSavedMsg, setVitalsSavedMsg] = useState(false);

  const loadVisit = async () => {
    try {
      const data = await visitsApi.get(id);
      setVisit(data);
      setNotes(data.clinical_notes || "");
      if (data.vitals) {
        setVitals({
          temperature: data.vitals.temperature || "",
          blood_pressure: data.vitals.blood_pressure || "",
          pulse: data.vitals.pulse || "",
          oxygen_saturation: data.vitals.oxygen_saturation || "",
          weight: data.vitals.weight || "",
        });
      }
    } catch (err) {
      setError(err.message || "Failed to load visit details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVisit();
  }, [id]);

  const handleNotesChange = (e) => {
    const val = e.target.value;
    setNotes(val);
    setSaveStatus("unsaved");

    if (notesTimeoutRef.current) {
      clearTimeout(notesTimeoutRef.current);
    }

    notesTimeoutRef.current = setTimeout(async () => {
      if (visit?.notes_finalized) return;
      setSaveStatus("saving");
      try {
        await visitsApi.saveNotes(id, val);
        setSaveStatus("saved");
        loadVisit();
      } catch (err) {
        setSaveStatus("error");
      }
    }, 1200);
  };

  const handleCheckIn = async () => {
    setActionError(null);
    try {
      const updated = await visitsApi.checkIn(id);
      setVisit(updated);
    } catch (err) {
      setActionError(err.detail || err.message);
    }
  };

  const handleCheckOut = async () => {
    if (!window.confirm("End this visit and finalize all clinical notes?")) return;
    setActionError(null);
    try {
      const updated = await visitsApi.checkOut(id, notes);
      setVisit(updated);
    } catch (err) {
      setActionError(err.detail || err.message);
    }
  };

  const handleTaskToggle = async (task) => {
    try {
      await visitsApi.toggleTask(id, task.id, !task.is_completed);
      loadVisit();
    } catch (err) {
      setActionError(err.detail || err.message);
    }
  };

  const handleAddTask = async (e) => {
    e.preventDefault();
    if (!newTaskTitle.trim()) return;
    setAddingTask(true);
    try {
      await visitsApi.addTask(id, newTaskTitle.trim());
      setNewTaskTitle("");
      loadVisit();
    } catch (err) {
      setActionError(err.detail || err.message);
    } finally {
      setAddingTask(false);
    }
  };

  const handleSaveVitals = async (e) => {
    e.preventDefault();
    setSavingVitals(true);
    setVitalsSavedMsg(false);
    try {
      await visitsApi.updateVitals(id, vitals);
      setVitalsSavedMsg(true);
      setTimeout(() => setVitalsSavedMsg(false), 3000);
      loadVisit();
    } catch (err) {
      setActionError(err.detail || err.message);
    } finally {
      setSavingVitals(false);
    }
  };

  if (loading) {
    return <PageSkeleton />;
  }

  if (error) {
    return (
      <PageShell>
        <PageBody>
          <Alert>{error}</Alert>
        </PageBody>
      </PageShell>
    );
  }

  if (!visit) {
    return (
      <PageShell>
        <PageBody>
          <EmptyState
            icon={SearchX}
            tone="slate"
            title="Visit not found"
            message="This visit may have been removed or is no longer available."
            action={
              <Button asChild variant="outline">
                <Link to="/schedule">Return to schedule</Link>
              </Button>
            }
          />
        </PageBody>
      </PageShell>
    );
  }

  const tasks = visit.tasks || [];
  const completedCount = tasks.filter((t) => t.is_completed).length;
  const progressPct = tasks.length > 0 ? Math.round((completedCount / tasks.length) * 100) : 0;

  const startDate = new Date(visit.start_time);
  const endDate = new Date(visit.end_time);

  const notesHint = visit.notes_finalized
    ? "Notes finalized"
    : saveStatus === "saving"
      ? "Saving draft..."
      : saveStatus === "saved"
        ? "Draft saved"
        : "Auto-saves as you type";

  return (
    <PageShell>
      <PageHeader
        title={visit.title || "Visit Documentation"}
        description={
          <span className="flex flex-col gap-1">
            <Link
              to="/schedule"
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              ← Back to Schedule
            </Link>
            <span className="inline-flex flex-wrap items-center gap-2">
              <Badge variant={visitStatusVariant(visit.status)} className="capitalize">
                {visit.status.replace("_", " ")}
              </Badge>
              <span>
                {startDate.toLocaleDateString(undefined, {
                  weekday: "long",
                  month: "short",
                  day: "numeric",
                })}{" "}
                •{" "}
                {startDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} –{" "}
                {endDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
            </span>
          </span>
        }
        actions={
          <>
            {visit.status !== "in_progress" &&
              visit.status !== "completed" &&
              visit.status !== "cancelled" && (
                <Button type="button" onClick={handleCheckIn}>
                  Start Visit (Check In)
                </Button>
              )}

            {visit.status === "in_progress" && (
              <Button type="button" onClick={handleCheckOut}>
                End Visit (Check Out)
              </Button>
            )}

            {visit.status === "completed" && (
              <Badge variant="default" className="px-3 py-1.5 text-sm">
                Visit Completed & Notes Finalized
              </Badge>
            )}
          </>
        }
      />

      <PageBody>
        <Alert>{actionError}</Alert>

        <Card className="bg-muted/30">
          <CardContent className="grid gap-4 sm:grid-cols-3">
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Client
              </div>
              <div className="mt-1 text-base font-semibold">{visit.client_name}</div>
              {visit.client_details?.formatted_address && (
                <div className="text-sm text-muted-foreground">
                  📍 {visit.client_details.formatted_address}
                </div>
              )}
            </div>

            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Assigned Caregiver
              </div>
              <div className="mt-1 text-base font-semibold">
                {visit.caregiver_name || "Unassigned"}
              </div>
              <div className="text-sm text-muted-foreground">
                Service: {visit.service_type}
              </div>
            </div>

            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Check-In / Out Timestamps
              </div>
              <div className="mt-1 text-sm">
                In:{" "}
                {visit.actual_start_time
                  ? new Date(visit.actual_start_time).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : "Not checked in"}
              </div>
              <div className="text-sm">
                Out:{" "}
                {visit.actual_end_time
                  ? new Date(visit.actual_end_time).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : "Not checked out"}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-4 lg:grid-cols-2">
          <div className="flex flex-col gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-base">Care Tasks Checklist</CardTitle>
                <span className="text-sm font-semibold text-muted-foreground">
                  {completedCount} of {tasks.length} Completed ({progressPct}%)
                </span>
              </CardHeader>
              <CardContent className="space-y-4">
                <Progress value={progressPct} />

                <div className="flex flex-col gap-2">
                  {tasks.map((task) => (
                    <label
                      key={task.id}
                      className="flex cursor-pointer items-center gap-3 rounded-lg bg-muted/50 px-3 py-2.5"
                    >
                      <Checkbox
                        checked={task.is_completed}
                        onCheckedChange={() => handleTaskToggle(task)}
                      />
                      <span
                        className={cn(
                          "text-sm",
                          task.is_completed && "text-muted-foreground line-through",
                        )}
                      >
                        {task.title}
                      </span>
                      {task.completed_by_name && (
                        <span className="ml-auto text-xs text-muted-foreground">
                          ✓ {task.completed_by_name}
                        </span>
                      )}
                    </label>
                  ))}
                </div>

                <form onSubmit={handleAddTask} className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="+ Add custom task item..."
                    value={newTaskTitle}
                    onChange={(e) => setNewTaskTitle(e.target.value)}
                  />
                  <Button type="submit" variant="outline" disabled={addingTask}>
                    Add
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-base">Clinical Visit Notes</CardTitle>
                <span className="text-xs text-muted-foreground">{notesHint}</span>
              </CardHeader>
              <CardContent>
                <Textarea
                  rows={6}
                  placeholder="Record observations, care provided, and client status..."
                  value={notes}
                  onChange={handleNotesChange}
                  disabled={visit.notes_finalized}
                />
              </CardContent>
            </Card>
          </div>

          <div className="flex flex-col gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Clinical Vitals</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {vitalsSavedMsg && <Alert type="info">Vitals saved</Alert>}

                <form onSubmit={handleSaveVitals} className="space-y-3">
                  <Field label="Temperature" name="temperature">
                    {(props) => (
                      <Input
                        {...props}
                        type="text"
                        placeholder="e.g. 98.6 °F"
                        value={vitals.temperature}
                        onChange={(e) =>
                          setVitals({ ...vitals, temperature: e.target.value })
                        }
                      />
                    )}
                  </Field>

                  <Field label="Blood Pressure" name="blood_pressure">
                    {(props) => (
                      <Input
                        {...props}
                        type="text"
                        placeholder="e.g. 120/80 mmHg"
                        value={vitals.blood_pressure}
                        onChange={(e) =>
                          setVitals({ ...vitals, blood_pressure: e.target.value })
                        }
                      />
                    )}
                  </Field>

                  <Field label="Pulse Rate" name="pulse">
                    {(props) => (
                      <Input
                        {...props}
                        type="text"
                        placeholder="e.g. 72 bpm"
                        value={vitals.pulse}
                        onChange={(e) => setVitals({ ...vitals, pulse: e.target.value })}
                      />
                    )}
                  </Field>

                  <Field label="Oxygen Saturation" name="oxygen_saturation">
                    {(props) => (
                      <Input
                        {...props}
                        type="text"
                        placeholder="e.g. 98%"
                        value={vitals.oxygen_saturation}
                        onChange={(e) =>
                          setVitals({ ...vitals, oxygen_saturation: e.target.value })
                        }
                      />
                    )}
                  </Field>

                  <Field label="Weight" name="weight">
                    {(props) => (
                      <Input
                        {...props}
                        type="text"
                        placeholder="e.g. 150 lbs"
                        value={vitals.weight}
                        onChange={(e) => setVitals({ ...vitals, weight: e.target.value })}
                      />
                    )}
                  </Field>

                  <Button type="submit" variant="outline" disabled={savingVitals}>
                    {savingVitals ? "Saving..." : "Save Vitals"}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Visit Timeline</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col gap-3">
                  {(visit.events || []).map((ev) => (
                    <div key={ev.id} className="border-l-2 border-border pl-3">
                      <div className="text-sm font-medium">{ev.description}</div>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {new Date(ev.timestamp).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    </div>
                  ))}
                  {(visit.events || []).length === 0 && (
                    <p className="text-sm text-muted-foreground">No timeline events yet.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </PageBody>
    </PageShell>
  );
}
